import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import clsx from "clsx";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import TicketOptionsMenu from "../TicketOptionsMenu";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Button, IconButton } from "../ui";

// Icons
const ReplayIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
    />
  </svg>
);

const MoreVertIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
  </svg>
);

const SpinnerIcon = ({ className }) => (
  <svg className={clsx("animate-spin", className)} viewBox="0 0 24 24" fill="none">
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

const TicketActionButtons = ({ ticket }) => {
  const history = useHistory();
  const [menuOpen, setMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user } = useContext(AuthContext);
  const isGroupTicket = ticket.isGroup === true;

  const handleOpenTicketOptionsMenu = () => {
    setMenuOpen(true);
  };

  const handleCloseTicketOptionsMenu = () => {
    setMenuOpen(false);
  };

  const handleUpdateTicketStatus = async (e, status, userId) => {
    setLoading(true);
    try {
      await api.put(`/tickets/${ticket.id}`, {
        status: status,
        userId: userId || null,
      });

      setLoading(false);
      if (status === "open") {
        history.push(`/tickets/${ticket.id}`);
      } else {
        history.push("/tickets");
      }
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  // Para grupos, não mostrar botões de ação (retornar, resolver, reabrir)
  // Apenas mostrar o menu com opção de deletar para admin
  if (isGroupTicket) {
    return (
      <div className="flex items-center gap-2 ml-auto">
        <div className="relative">
          <IconButton onClick={handleOpenTicketOptionsMenu}>
            <MoreVertIcon className="w-5 h-5" />
          </IconButton>
          <TicketOptionsMenu
            ticket={ticket}
            menuOpen={menuOpen}
            handleClose={handleCloseTicketOptionsMenu}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 ml-auto">
      {ticket.status === "closed" && (
        <Button
          size="small"
          variant="outlined"
          disabled={loading}
          onClick={(e) => handleUpdateTicketStatus(e, "open", user?.id)}
        >
          {loading ? (
            <SpinnerIcon className="w-4 h-4 mr-2" />
          ) : (
            <ReplayIcon className="w-4 h-4 mr-2" />
          )}
          {i18n.t("messagesList.header.buttons.reopen")}
        </Button>
      )}

      {ticket.status === "open" && (
        <>
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            disabled={loading}
            onClick={(e) => handleUpdateTicketStatus(e, "pending", null)}
          >
            {loading ? (
              <SpinnerIcon className="w-4 h-4 mr-2" />
            ) : (
              <ReplayIcon className="w-4 h-4 mr-2" />
            )}
            {i18n.t("messagesList.header.buttons.return")}
          </Button>
          <Button
            size="small"
            disabled={loading}
            onClick={(e) => handleUpdateTicketStatus(e, "closed", user?.id)}
          >
            {loading && <SpinnerIcon className="w-4 h-4 mr-2" />}
            {i18n.t("messagesList.header.buttons.resolve")}
          </Button>

          {/* Menu Button with relative container */}
          <div className="relative">
            <IconButton onClick={handleOpenTicketOptionsMenu}>
              <MoreVertIcon className="w-5 h-5" />
            </IconButton>
            <TicketOptionsMenu
              ticket={ticket}
              menuOpen={menuOpen}
              handleClose={handleCloseTicketOptionsMenu}
            />
          </div>
        </>
      )}

      {ticket.status === "pending" && (
        <Button
          size="small"
          disabled={loading}
          onClick={(e) => handleUpdateTicketStatus(e, "open", user?.id)}
        >
          {loading && <SpinnerIcon className="w-4 h-4 mr-2" />}
          {i18n.t("messagesList.header.buttons.accept")}
        </Button>
      )}
    </div>
  );
};

export default TicketActionButtons;
